/**
 * The page a customer lands on the moment they sign.
 *
 * It replaced "ALL DONE — Signed. Thank you, we will be in touch straight
 * away", which was the end of the road: the customer had nothing to do, and the
 * request for their deposit turned up from a bank they had never heard of,
 * whenever a rep next opened the job. Jonathon, 2026-09-06 — the signature
 * should flow straight into paying.
 *
 * Three cards: apply for financing, pay by credit card with the 3% shown, pay
 * by bank transfer with no fee. The word "debit" appears nowhere, because
 * surcharging one is illegal and offering it would be offering something the
 * company cannot charge for.
 *
 * Nothing on this page works out a number. `paymentChoices` in packages/core
 * decides what the cards say, what they cost and which of them exist at all
 * (CLAUDE.md rule 5), and the server decides what is actually billed. This is
 * the picture of that.
 */

import { paymentChoices, type PaymentMethod } from "@roof-hawk/core";
import { useState } from "react";

import { COMPANY_LOGO_URL } from "../../logo";

/** What the Edge Function tells the page about the money. */
export interface PayBlock {
  dueCents: number;
  contractCents: number;
  /**
   * 1 is the deposit at signing, 4 the balance once the work is finished, and
   * null a payment that is on no schedule at all — the balance a customer is
   * offered when they approve a change order (ADR 0141, ADR 0195).
   */
  slot: number | null;
  /**
   * What they have just signed, which decides the words on this page. Absent
   * means the contract, which is what this page was for until 2026-09-22.
   */
  purpose?: "contract" | "change-order";
  /** Whether it is owed today, or simply offered early on a small job. */
  dueNow: boolean;
  chosen: PaymentMethod | null;
  paid: boolean;
  cancelled: boolean;
  paymentUrl: string | null;
  financingUrl: string | null;
}

export interface PaymentChoicePageProps {
  pay: PayBlock;
  /**
   * Records the choice and comes back with somewhere to send them. Raising the
   * invoice is the server's job — the amount depends on the method, and a
   * browser is not allowed to assert either.
   */
  onChoose: (
    method: PaymentMethod,
  ) => Promise<{ paymentUrl?: string | null; error?: string | undefined }>;
}

export function PaymentChoicePage({ pay, onChoose }: PaymentChoicePageProps) {
  const [busy, setBusy] = useState<PaymentMethod | null>(null);
  const [error, setError] = useState<string | null>(null);

  const choices = paymentChoices({
    due: pay.dueCents as never,
    contract: pay.contractCents as never,
    dueNow: pay.dueNow,
    slot: pay.slot ?? undefined,
    purpose: pay.purpose,
    financingUrl: pay.financingUrl,
  });

  const choose = async (method: PaymentMethod) => {
    setBusy(method);
    setError(null);

    // Financing goes to somebody else's site and the payment stays open, so it
    // is the one that does not wait for a link to come back.
    if (method === "financing") {
      const url = pay.financingUrl;
      void onChoose(method);
      if (url) window.location.href = url;
      setBusy(null);
      return;
    }

    const result = await onChoose(method);
    if (result.paymentUrl) {
      // Mercury's own secure page. Replacing rather than opening a tab: a
      // customer on a phone who loses the popup has lost the payment, and the
      // link that brought them here still works if they come back.
      window.location.href = result.paymentUrl;
      return;
    }
    setError(
      result.error ??
        "We could not set that up just now. Please try again, or call us and " +
          "we will take it over the phone.",
    );
    setBusy(null);
  };

  if (pay.paid) {
    return (
      <Shell heading="Thank you — you are all set">
        <p className="pdoc-choose-intro">
          {pay.purpose === "change-order"
            ? "Your payment has come through. Thank you — we will carry on " +
              "with your work."
            : "Your payment has come through and your job is booked in. We " +
              "will be in touch to arrange your dates."}
        </p>
      </Shell>
    );
  }

  return (
    <Shell heading={choices.heading}>
      <p className="pdoc-choose-intro">{choices.intro}</p>

      {/* Somebody who has already picked and come back. The card they chose is
          still there to finish, rather than a page insisting they choose
          again. */}
      {pay.chosen && pay.paymentUrl ? (
        <p className="pdoc-choose-back">
          You chose to pay by{" "}
          {pay.chosen === "card" ? "credit card" : "bank transfer"}.{" "}
          <a href={pay.paymentUrl}>Finish your payment</a>.
        </p>
      ) : null}

      {error ? (
        <p className="pdoc-choose-error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="pdoc-choose-cards">
        {choices.cards.map((card) => (
          <button
            key={card.method}
            type="button"
            className={`pdoc-choose-card pdoc-choose-${card.method}`}
            disabled={busy !== null}
            onClick={() => void choose(card.method)}
          >
            <span className="pdoc-choose-title">{card.title}</span>
            {card.amountFormatted ? (
              <span className="pdoc-choose-amount">{card.amountFormatted}</span>
            ) : null}
            <span className="pdoc-choose-blurb">{card.blurb}</span>
            <span className="pdoc-choose-action">
              {busy === card.method ? "One moment…" : card.action}
            </span>
          </button>
        ))}
      </div>

      <p className="pdoc-choose-note">
        Payments are taken on a secure page hosted by our bank. We never see or
        store your card or account details.
      </p>
    </Shell>
  );
}

function Shell({
  heading,
  children,
}: {
  heading: string;
  children: React.ReactNode;
}) {
  return (
    <div className="pdoc pdoc-choose">
      <header className="pdoc-choose-head">
        {/* The company's mark, never Roof Board's — the customer has never
            heard of Roof Board and should not start here. */}
        <img className="pdoc-choose-logo" src={COMPANY_LOGO_URL} alt="" />
      </header>
      <main className="pdoc-choose-body">
        <h1>{heading}</h1>
        {children}
      </main>
    </div>
  );
}
